import { Module } from "vuex";
import Notify from 'quasar/src/plugins/Notify.js';;
import i18n from "@/i18n";
import { logger } from "@/utils/logger";
import { StaticTemplateRow, StaticTemplateState } from "@/types";
import {
  createStaticTemplate,
  fetchStaticTemplates,
  printStaticTemplate,
  queryStaticTemplates,
  resolveOriginalFileName,
  toOriginalFileName,
} from "@/store/services/configuration/staticTemplateService";

const staticTemplateModule: Module<StaticTemplateState, unknown> = {
  namespaced: true,
  state: {
    isCreating: false,
    loaded: false,
    rows: [],
  },
  getters: {
    isCreating: (state) => state.isCreating,
  },
  mutations: {
    setCreating(state, payload: boolean) {
      state.isCreating = payload;
    },
    setLoaded(state, payload: boolean) {
      state.loaded = payload;
    },
    setRows(state, payload: StaticTemplateRow[]) {
      state.rows = payload || [];
    },
  },
  actions: {
    async searchStaticTemplate({ state, commit }, payload) {
      const { filter, offset, limit, callback, refresh } = payload;
      try {
        if (refresh || !state.loaded) {
          const rows = await fetchStaticTemplates();
          commit("setRows", rows);
          commit("setLoaded", true);
        }
      } catch (error) {
        console.error(error);
        commit("setRows", []);
        commit("setLoaded", false);
      }
      const response = queryStaticTemplates(state.rows, filter, offset, limit);
      callback?.(response);
      return response;
    },
    async downloadStaticTemplate(
      { commit, state },
      payload: { templateCode: string; file: File; originalFileName?: string }
    ) {
      commit("setCreating", true);
      try {
        const originalFileName =
          toOriginalFileName(payload.originalFileName) ||
          resolveOriginalFileName(state.rows, payload.templateCode, payload.file.name);
        const result = await printStaticTemplate({
          templateCode: payload.templateCode,
          originalFileName,
          file: payload.file,
        });
        if (!result.downloaded) {
          throw new Error("empty print file");
        }
        Notify.create({
          type: "positive",
          message: i18n.global.t("staticTemplate.downloadSuccess"),
          timeout: 4000,
        });
      } catch (error) {
        logger.error(error);
        Notify.create({
          type: "negative",
          message: i18n.global.t("staticTemplate.downloadError"),
          timeout: 5000,
        });
        throw error;
      } finally {
        commit("setCreating", false);
      }
    },
    async uploadStaticTemplates(
      { commit },
      payload: {
        items: Array<{ key: string; templateCode: string; originalFileName: string; file: File }>;
        onProgress?: (update: { key: string; status: "running" | "success" | "error" }) => void;
      }
    ) {
      commit("setCreating", true);
      try {
        for (const item of payload.items || []) {
          payload.onProgress?.({ key: item.key, status: "running" });
          try {
            await createStaticTemplate({
              templateCode: item.templateCode,
              originalFileName: item.originalFileName,
              file: item.file,
              saveFile: false,
            });
            payload.onProgress?.({ key: item.key, status: "success" });
          } catch (error) {
            console.error(error);
            logger.error(error);
            payload.onProgress?.({ key: item.key, status: "error" });
          }
        }
        commit("setLoaded", false);
      } finally {
        commit("setCreating", false);
      }
    },
    async createStaticTemplate(
      { commit, state },
      payload: { templateCode: string; file: File; originalFileName?: string; saveFile?: boolean }
    ) {
      commit("setCreating", true);
      try {
        const originalFileName =
          toOriginalFileName(payload.originalFileName) ||
          resolveOriginalFileName(state.rows, payload.templateCode, payload.file.name);
        await createStaticTemplate({
          templateCode: payload.templateCode,
          file: payload.file,
          originalFileName,
          saveFile: payload.saveFile,
        });
        commit("setLoaded", false);
        Notify.create({
          type: "positive",
          message: i18n.global.t("staticTemplate.addSuccess"),
          timeout: 4000,
        });
      } catch (error) {
        logger.error(error);
        Notify.create({
          type: "negative",
          message: i18n.global.t("staticTemplate.addError"),
          timeout: 5000,
        });
        throw error;
      } finally {
        commit("setCreating", false);
      }
    },
  },
};

export default staticTemplateModule;
